import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import usePopoverPosition from "../../../hooks/usePopoverPosition";
import useSzerokoscElementu from "../../../hooks/useSzerokoscElementu";
import styles from "./MultiSelectCombo.module.css";
import { useWarstwaNadModalem } from "../../SideDrawer/warstwaNadModalem";

/**
 * Uniwersalny multi-select combo (BRIEF-0010/0012): przycisk z chipami wybranych pozycji
 * + rozwijany panel z checkboxami. Wyszukiwarka pojawia się automatycznie przy >6 opcjach.
 * Panel zamyka się klikiem poza komponentem; chipy można usuwać bez otwierania panelu.
 * Motyw przez var(--color-*) — spójny z resztą aplikacji (light/dark).
 *
 * Props:
 *  - options: [{ value, label }] (value: string|number — porównywane po String())
 *  - values:  lista zaznaczonych value
 *  - onChange(newValues): pełna nowa lista zaznaczeń
 *  - placeholder: tekst gdy nic nie zaznaczono
 *  - disabled, searchThreshold (domyślnie 6), emptyText
 *  - nadModalem: warstwa panelu (patrz niżej przy definicji propsa)
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * [2026-09-15, po weryfikacji adwersarialnej listy kategorii] PANEL IDZIE W PORTAL.
 *
 * CO BYŁO ŹLE: panel stał `position: absolute` w obudowie pola, a w karcie zgłoszenia ta obudowa
 * siedzi w `.sidebar` z `overflow-y: auto` (TicketDetailsModal.module.css). Dokładnie ten sam
 * przycinacz, spod którego lista kategorii wyszła portalem — a oba combo („Firma (klasyfikacja)”
 * i „Przypisz do”) zostały pod nim. „Przypisz do” mapuje CAŁE `assignableUsers` z serwera, więc
 * jest długie i realnie ucinane dolną krawędzią panelu.
 *
 * DECYZJE — te same co przy PoleKategorii, żeby w jednym oknie nie stały dwa mechanizmy:
 *  • panel renderuje się w PORTALU do <body> (wyjście spod `overflow` ORAZ spod `transform`,
 *    który `.sidebar` dostaje poniżej 1024 px — niezerowy transform robi z przodka blok
 *    zawierający dla `position: fixed`, więc samo `fixed` bez portalu naprawiłoby desktop
 *    i zepsuło tablet);
 *  • współrzędne i sufit liczy `hooks/usePopoverPosition` (pomiar przed malowaniem, odbicie
 *    w górę przy dolnej krawędzi okna, `maxHeight` z realnie wolnego miejsca);
 *  • SUFIT WŁASNY, MNIEJSZY NIŻ PRZY KATEGORIACH — `SUFIT_OKNA` niżej; pozycje są tu
 *    JEDNOWIERSZOWE, więc ten sam ułamek okna co przy kategoriach byłby rozjazdem;
 *  • szerokość MIERZYMY (po portalu nie ma już `left: 0; right: 0`);
 *  • ⚠ KLIK W PANEL TO KLIK „WEWNĄTRZ” — panel nie jest już potomkiem `rootRef`, więc bez
 *    osobnego warunku `mousedown` na opcji zamykałby panel ZANIM zdążyłby zadziałać `onClick`
 *    (zaznaczanie przestałoby działać w ogóle).
 *
 * SUFIT WYSOKOŚCI — ILE TO JEST W POZYCJACH (1 rem = 14 px, `html { font-size: 87.5% }`;
 * line-height 1.6 z global.css):
 *   • `.option`: wyściółka 0.4375rem × 2 = 12,25 px + tekst 0.7781rem × 1,6 ≈ 17,4 px ≈ 30 px
 *     (JEDEN wiersz — w liście kategorii pozycja ma dwa i wychodzi ≈ 45 px);
 *   • `.optionList`: wyściółka 0.25rem × 2 = 7 px;
 *   • `.searchRow` (od 7 opcji w górę): 0.5rem × 2 = 14 px + 0.7687rem × 1,6 ≈ 17,2 + kreska 1 ≈ 32 px.
 *   Czyli 15 pozycji ≈ 15 × 30 + 7 + 32 ≈ 489 px, a 0,5 okna daje 500 px przy oknie 1000 px
 *   i 540 px przy 1080 px (~17 pozycji). Dawne `max-height: 13.75rem` = 192,5 px ≈ 6 pozycji.
 *   Rozjazd wobec listy kategorii (0,75 okna): 1,5× — a nie 3-4×, jak przy dwóch różnych sufitach
 *   wpisanych na sztywno. Ułamek, nie rem: sufit dotyczy wysokości OKNA, a suwak dostępności
 *   skaluje rem, więc sufit w rem przy 125 % wyjechałby poza ekran.
 */
const SUFIT_OKNA = 0.5;

const MultiSelectCombo = ({
    options = [],
    values = [],
    onChange,
    placeholder = "— wybierz —",
    disabled = false,
    searchThreshold = 6,
    emptyText = "Brak opcji",
    /* Warstwa panelu — dokładnie ta sama decyzja i ten sam props co w DatePicker `nadModalem`.
       Panel jest w portalu, więc o kolejność nie zadba zagnieżdżenie: domyślne
       --z-modal-dropdown (10000) leży PONIŻEJ panelu szuflady otwartej z wnętrza okna modalnego
       (--z-drawer-over-modal, 10011). Włączane świadomie i miejscowo — podniesienie warstwy
       globalnie wypchnęłoby combo nad szuflady na wszystkich ekranach, które go używają.
       [2026-10-01] Bez propsa decyduje kontekst WarstwaNadModalem (szuflada nad oknem / okno z SzufladyNadModalem). */
    nadModalem: nadModalemJawny,
}) => {
    const nadModalem = useWarstwaNadModalem(nadModalemJawny);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    // Kotwicą jest przycisk z chipami — panel ma stanąć pod nim i mieć jego szerokość.
    const triggerRef = useRef(null);
    const [ustawTrigger, szerokoscTriggera] = useSzerokoscElementu(triggerRef, { pomiar: "outer" });

    const { style: stylPanelu } = usePopoverPosition(isOpen, triggerRef, panelRef, {
        fit: true,
        minHeight: 160,
        sufitUlamekOkna: SUFIT_OKNA,
    });

    const selectedSet = useMemo(() => new Set(values.map((v) => String(v))), [values]);

    // Zamknięcie panelu klikiem poza komponentem (mousedown — jak reporterBox w NewTicketModal).
    useEffect(() => {
        const handleMouseDown = (e) => {
            if (!rootRef.current || rootRef.current.contains(e.target)) return;
            // ⚠ Panel stoi w PORTALU do <body>, więc DOM-owo nie jest już w `rootRef`. Bez tego
            // warunku `mousedown` na opcji zamykałby panel przed `onClick` — czyli zaznaczanie
            // i pasek przewijania przestałyby działać.
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            setIsOpen(false);
            setSearch("");
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, []);

    const showSearch = options.length > searchThreshold;

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => String(o.label || "").toLowerCase().includes(q));
    }, [options, search]);

    const selectedOptions = useMemo(
        () => options.filter((o) => selectedSet.has(String(o.value))),
        [options, selectedSet]
    );

    const toggleValue = (value) => {
        const key = String(value);
        const next = selectedSet.has(key)
            ? values.filter((v) => String(v) !== key)
            : [...values, value];
        onChange(next);
    };

    const removeValue = (e, value) => {
        e.stopPropagation();
        if (disabled) return;
        onChange(values.filter((v) => String(v) !== String(value)));
    };

    return (
        <div className={styles.combo} ref={rootRef}>
            <button
                type="button"
                ref={ustawTrigger}
                className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""}`}
                onClick={() => { if (!disabled) { setIsOpen((p) => !p); setSearch(""); } }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={styles.triggerContent}>
                    {selectedOptions.length === 0 ? (
                        <span className={styles.placeholder}>{placeholder}</span>
                    ) : (
                        selectedOptions.map((o) => (
                            <span key={String(o.value)} className={styles.chip}>
                                <span className={styles.chipLabel}>{o.label}</span>
                                <span
                                    role="button"
                                    tabIndex={-1}
                                    className={styles.chipRemove}
                                    onClick={(e) => removeValue(e, o.value)}
                                    title={`Usuń: ${o.label}`}
                                >
                                    <FiX size={11} />
                                </span>
                            </span>
                        ))
                    )}
                </span>
                <FiChevronDown className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`} size={14} />
            </button>

            {isOpen && createPortal(
                <div
                    className={`${styles.panel} ${nadModalem ? styles.panelNadModalem : ""}`}
                    role="listbox"
                    ref={panelRef}
                    /* `stylPanelu` z hooka: position/top/left, `maxHeight` = min(0,5 okna, wolne
                       miejsce) i `visibility: hidden` do czasu pomiaru (panel nie mignie w rogu).
                       Szerokość dokładamy z pomiaru kotwicy — hook jej nie zwraca, a po wyjściu
                       z obudowy nie ma już `left: 0; right: 0`, które ją dawniej rozciągało. */
                    style={szerokoscTriggera ? { ...stylPanelu, width: szerokoscTriggera } : stylPanelu}
                    data-panel-multiselect
                >
                    {showSearch && (
                        <div className={styles.searchRow}>
                            <FiSearch size={13} className={styles.searchIcon} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Szukaj..."
                                autoFocus
                            />
                        </div>
                    )}
                    <ul className={styles.optionList}>
                        {filteredOptions.length === 0 && (
                            <li className={styles.noOptions}>{search ? "Brak wyników." : emptyText}</li>
                        )}
                        {filteredOptions.map((o) => {
                            const checked = selectedSet.has(String(o.value));
                            return (
                                <li key={String(o.value)}>
                                    <button
                                        type="button"
                                        className={`${styles.option} ${checked ? styles.optionChecked : ""}`}
                                        onClick={() => toggleValue(o.value)}
                                        role="option"
                                        aria-selected={checked}
                                    >
                                        <span className={styles.checkbox}>
                                            {checked && <FiCheck size={11} />}
                                        </span>
                                        <span className={styles.optionLabel}>{o.label}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MultiSelectCombo;
